import { Dialog } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import YupPassword from 'yup-password';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { Input } from '@/components/form/input';

import { resetPassword } from '../services/auth';

// extend yup to support password validations
YupPassword(yup);

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;

  /** password reset token */
  token: string;
}

export type ResetPasswordFormFields = {
  password1: string;
  password2: string;
};

const validationSchema = yup.object({
  password1: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must contain 8 or more characters.')
    .minLowercase(1, 'Password must contain a minimum of 1 lower case letter [a-z]')
    .minUppercase(1, 'Password must contain a minimum of 1 upper case letter [A-Z].')
    .minNumbers(1, 'Password must contain a minimum of 1 numeric character [0-9].'),
  password2: yup
    .string()
    .oneOf([yup.ref('password1'), null], 'Password confirmation must match password.'),
});

export const ResetPasswordModal: React.FC<IProps> = ({ isOpen, toggle, token }) => {
  const { t } = useTranslation('translation', { useSuspense: false });
  const navigate = useNavigate();

  const updatePasswordMutation = useMutation({
    mutationFn: ({ ...data }: { token: string; password1: string; password2: string }) =>
      resetPassword({ ...data }),
    onSuccess: () => {
      toast.success(t('forgotPassword.successPasswordReset'));
    },
    onError: () => {
      toast.error(t('forgotPassword.errorPasswordReset'));
    },
    onSettled: () => {
      onReset();
    },
  });

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormFields>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((data) => {
    updatePasswordMutation.mutate({ token, ...data });
  });

  const onReset = () => {
    reset();
    toggle();
    navigate('/');
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title={t('forgotPassword.changePasswordTitle')}
    >
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-3 py-6">
          <Dialog.Description>
            Changing your password will sign you out of all your devices, including
            android APPs. You will need to enter your new password on all your devices.
          </Dialog.Description>
          <Input<ResetPasswordFormFields>
            variant="solid"
            type="password"
            name="password1"
            className="w-72"
            autoComplete="new-password"
            placeholder={t('forgotPassword.password')}
            register={register}
            errors={errors}
          />
          <Input<ResetPasswordFormFields>
            variant="solid"
            type="password"
            name="password2"
            className="w-72"
            autoComplete="new-password"
            placeholder={t('forgotPassword.confirm')}
            register={register}
            errors={errors}
          />
        </div>
        <div className="flex justify-end">
          <Button type="submit" isLoading={updatePasswordMutation.isLoading}>
            {t('forgotPassword.request')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
